import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
//components
import { MemoizedMarker } from './MemoizedMarker';
import { PigMarker } from './PigMarker';
import { MapControl } from './MapControl';
import greenMarker from '../../assets/icons/green-marker.png';
import subchainMarker from '../../assets/icons/subchain-marker.png';
import poiMarker from '../../assets/icons/flag.png';
import redMarker from '../../assets/icons/red-marker.png';
import draggableMarker from '../../assets/icons/draggableMarker3.png';
import { GoogleMapComponent } from './GoogleMapComponent';
import Label from '../ui/labels/Label';
import { SelectContainer } from '../ui/layout/SelectContainer';
import Select from 'react-select'
import { RunMessages } from '../messages/RunMessages';
import { LiveTrackControl } from '../liveTrack/liveTrackControls/LiveTrackControl';
import { Button } from '@components/ui/buttons/Button';
import { CustomIntersectModal } from './modals/pois/poiDialogComponents/CustomIntersectModal';
//hooks
import useLiveSiteMap from '../../hooks/useLiveSiteMap';
import useLiveTrackData from '../../hooks/useLiveTrackData';
import usePoiMap from '../../hooks/usePoiMap';
//contexts
import { useMapControl } from '../../contexts/MapControlContext';
import './css/map.css'
import { LiveTrackSummary } from '../liveTrack/LiveTrackSummary';
import { RunMessagesProvider } from '../../contexts/runMessages/RunMessageContext';
import { useUser } from '@contexts/UserContext';
import { useError } from '@contexts/ErrorContext';
//utils
import { isDraggableMarkerDisabled } from '../../utils/poi/isPoiEditDisabled';
import { convertToLocalTime } from '@utils/dateAndTimeUtils';
import { getAGMMarkerPosition, isValidCoordinate } from '@utils/locationUtils';

const containerStyle = {
  height: '75vh'
};

const polylineOptions = {
  strokeColor: '#0000FF',
  strokeOpacity: 0.8,
  strokeWeight: 2,
}; // Move static options outside of the component

/**
 * Renders items on google map
 * 
 * @param {DropdownOption} selectedJob 
 * @param {DropdownOption | null} selectedRun 
 * @param {Boolean} liveWPig 
 * @param {Boolean} poiMap 
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Map
 */

const MapComponent = ({ 
  selectedJob, 
  selectedRun = null, 
  liveWPig, 
  poiMap = false, 
  openPoiOnMap = null, 
  storedPoiGroup = null,
  setStoredPoiGroup = null,
  timezone
}) => {
  const { mapControl } = useMapControl(); //toggles for sc and agm
  let {
    subchains,
    agms,
    fetchRouteArr,
    polyLineArr,
    isFetching,

    //testing here
    // mergeLiveTrackWithRouteArr,
    // findAndStoreLastUpdateValue,
    // mergedLTandRoute,
    // routeArrRef
  } = useLiveSiteMap();

// useEffect(() => {
//     let interval = null;
//     if (selectedRun) {
//       interval = setInterval(() => {
//         checkLiveTrackForUpdates(selectedRun.value.toString());
//         setDataUpdated(new Date())
//       }, 10000); // 10000 milliseconds = 10 seconds
//     }
//     // Cleanup function to clear the interval when the component unmounts or the selectedRun changes
//     return () => {
//       if (interval) {
//         clearInterval(interval);
//       }
//     };
//   }, [selectedRun])

  // useEffect(() => {
  //   if (selectedJob) {
  //     fetchRouteArr(selectedJob.value.toString());
  //   }
  // }, [selectedJob]); //eslint-disable-line

  // const {
  //   mergeLiveTrackWithRouteArr,
  //   checkLiveTrackForUpdates,
  //   findAndStoreLastUpdateValue,
  //   mergedLTandRoute,
  //   routeArrRef,
  //   fetchRouteArr,
  //   agms,
  //   // noData
  // } = useLiveSiteMap();

  const {
    formattedLiveTrackTable,
    fetchLiveTrackData,
    checkLiveTrackForUpdates,
    runData,
    runMessageCount,
    notificationsPaused,
    setNotificationsPaused
  } = useLiveTrackData();

  const mapRef = useRef(null); //ref to ggl map
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [center, setCenter] = useState(null); //center of map

  const [scData, setScData] = useState(subchains);
  const [agmData, setAgmData] = useState(agms);

  const [dataUpdated, setDataUpdated] = useState() //pig marker updates this so live track summary can update at the same time
  const hasFetchedUpdatesRef = useRef(false);

  //subchains set in hook
  useEffect(() => {
    setScData(subchains);
  }, [subchains]);

  //agms set in hook
  useEffect(() => {
    setAgmData(agms);
  }, [agms])

  // Job Selected - fetch the route arr
  // this is also called in PigMarker component
  // context causes bugs and too many re-renders
  // opted for 2 api calls
  useEffect(() => {
    if (selectedJob) {
      fetchRouteArr(selectedJob.value.toString());

      if (poiMap) {
        setSelectedPoiJob(selectedJob.value.toString())
      }
    }
  }, [selectedJob]); //eslint-disable-line

  //get estimated pass time for agm markers, from formattedLiveTrackTable
  const getEstimatedPassTime = (AGMID, liveTrackTable) => {
    const currentAGM = liveTrackTable.find(agm => agm.AGMID === AGMID)

    const estPassTime = currentAGM?.Est_PassTime

    if (!currentAGM || !estPassTime) return null

    return estPassTime
  }

  //memoize agm positions when agmData and formattedLiveTrackTable changes
  const memoizedAGMPositions = useMemo(() =>
    agmData.map(agm => {
      //Real lat/lng takes priority over Est - see getAGMMarkerPosition
      const { lat, lng } = getAGMMarkerPosition(agm);

      return {
        lat: Number(lat),
        lng: Number(lng),
        id: agm.AGMID,
        title: agm.Site_Name,
        PassTime: agm.PassTime ? agm.PassTime : null,
        Est_PassTime: getEstimatedPassTime(agm.AGMID, formattedLiveTrackTable),
        Approved: agm.Approved
      };
    })
      //an AGM with no lat/lng filled in (or left at 0) has no real location - drop it so it
      //isn't drawn off the coast of Africa and doesn't drag the map center/bounds with it
      .filter(agm => isValidCoordinate(agm.lat) && isValidCoordinate(agm.lng)
        && !Number.isNaN(agm.lat) && !Number.isNaN(agm.lng)),
    [agmData, formattedLiveTrackTable]
  );

  //memoize subchain positions when scData changes
  const memoizedSCPositions = useMemo(() =>
    scData.map((sc, index) => ({
      lat: sc.lat,
      lng: sc.lng,
      id: sc.SCID,
      SC_Name: sc.SC_Name,
      index
    })),
    [scData]
  );

  const memoizedRoute = useMemo(() => polyLineArr, [polyLineArr]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    // googleMapsApiKey: process.env.REACT_APP_GGL_MAP_API_KEY
    googleMapsApiKey: 'AIzaSyCxEeZ8ULjjhvNzn0j-WlNDqtavdt8ht34' //temp - bugs present when trying to env variable
  });

  //get the center of the map
  useEffect(() => {
    if (!center && memoizedAGMPositions.length > 0) {
      const first = memoizedAGMPositions[0];
      const last = memoizedAGMPositions[memoizedAGMPositions.length - 1];
      
      setCenter({
        lat: (first.lat + last.lat) / 2,
        lng: (first.lng + last.lng) / 2
      });

      // Calculate bounds for all AGMs
      //proper zoom to fit whole pipeline in initially
      const bounds = new window.google.maps.LatLngBounds();
      memoizedAGMPositions.forEach(pos => {
        bounds.extend({ lat: pos.lat, lng: pos.lng });
      });

      mapRef.current.fitBounds(bounds);
    }
  }, [memoizedAGMPositions]); //eslint-disable-line 

  // Initial fetch of live track data when run selected
  useEffect(() => {
    if (selectedRun) {
      // getRunData();
      fetchLiveTrackData(selectedRun.value)
      setSelectedPoiRun(selectedRun.value)
      hasFetchedUpdatesRef.current = false;
    }
  }, [selectedRun]); //eslint-disable-line

  // Once live track data has loaded, make sure RunMessages/LiveTrackSummary
  // receive the latest run/update info immediately (no need to wait for interval).
  useEffect(() => {
    if (!selectedRun || formattedLiveTrackTable.length === 0 || hasFetchedUpdatesRef.current) return;

    checkLiveTrackForUpdates(selectedRun.value.toString());
    setDataUpdated(new Date());
    hasFetchedUpdatesRef.current = true;
  }, [selectedRun, formattedLiveTrackTable]); //eslint-disable-line

  // Poll for updates every 10 seconds
  useEffect(() => {
    if (!selectedRun) return;

    const interval = setInterval(() => {
      checkLiveTrackForUpdates(selectedRun.value.toString());
      setDataUpdated(new Date());
    }, 10000); // 10000 milliseconds = 10 seconds

    return () => {
      clearInterval(interval);
    };
  }, [selectedRun]); //eslint-disable-line

  const onLoad = (map) => {
    mapRef.current = map;
    if (!isMapInitialized) {
      setIsMapInitialized(true); // Set the flag only after the first load
    }
  };

  const onUnmount = () => {
    mapRef.current = null;
  };

  /************************************************************ */
  /* Clicking Map for POIs */
  /************************************************************ */

  const {
    handleMarkerClick,
    clickedPoints,
    draggableMarkerPosition,
    handleMarkerDrag,
    handleMapClick,
    setSelectedPoiJob,
    selectedPoiGroup,
    setSelectedPoiGroup,
    poiGroups,
    permissionSortedPoiGroups,
    currentClickedPoint,
    setSelectedPoiRun,
    handleCreatePointFromCustomIntersect,
    handleDialogCancel
  } = usePoiMap({ mapRef, openPoiOnMap, polyLineArr });

  const [showCustomIntersectModal, setShowCusomIntersectModal] = useState(false);
  const [customIntersectError, setCustomIntersectError] = useState('');
  const {showError} = useError();

  const handleOpenCustomIntersectModal = async() => {
    if(
        (selectedPoiGroup && !selectedPoiGroup.label.includes("All POIs")) &&
        (!selectedPoiGroup.IsUserInGroup || !selectedPoiGroup.IsEditor)
    ){
        showError(`To create POIs in the group "${selectedPoiGroup.label}", you must be an editor. Please select a group in which you are an editor, or select "All POIs for job" to create POIs.`)
        return   
    }
            
    const {isClosed} = await handleDialogCancel()

    if(!isClosed) return

    setShowCusomIntersectModal(true)
  }

  const handleCustomIntersectSubmit = async(lat, lng) => {
    if (!poiMap) return;


    const result = await handleCreatePointFromCustomIntersect({lat, lng}, polyLineArr)

    if(!result.success && result.errorMessage){
      //validation message for modal
      setCustomIntersectError(result.errorMessage)

      return {
        success: false
      }
    }

    setShowCusomIntersectModal(false)
    setCustomIntersectError('')

    return {
      success: true
    }

  }

    /** SELECTED POI GROUP **/

  //if one has been selected on poi-edit page, select it here too
  useEffect(() => {
    if(storedPoiGroup){
      setSelectedPoiGroup(storedPoiGroup)
    }
  },[storedPoiGroup])//eslint-disable-line

  //if we selected a different one on the map, and we are on the poi-edit page (have setStoredPoiGroup available to us), then store the group
  useEffect(() => {
    if(selectedPoiGroup && setStoredPoiGroup){
      setStoredPoiGroup(selectedPoiGroup)
    }
  },[selectedPoiGroup])//eslint-disable-line

  /** PERMISSIONS FOR DRAGGABLE MARKER **/

  //conditions:
    //if on site-map or site-map w/ pig, never show draggable marker - editing only allowed on poi-edit page
    //poi creator can update intersection point only if POI is not in any groups - don't want to mess up group users' notifications
    //admin users can edit intersection point at any time - in case a mistake was made during POI initial creation
  const {userGroups, user} = useUser()
  const [poiIsInGroups, setPoiIsInGroups] = useState()
  const [editDisabled, setEditDisabled] = useState()
  const isAdmin = userGroups?.includes("Admin")

  const currentPage = window.location.pathname
  const canSelectPOIs = selectedJob && selectedJob.IsLocked !== false;

  useEffect(() => {
    if(!currentClickedPoint) return

    setPoiIsInGroups(currentClickedPoint.linkedPOIs.length > 0)

    const isDisabled = currentClickedPoint.CreatedBy ? isDraggableMarkerDisabled(currentPage, currentClickedPoint.CreatedBy, { userGroups, user }) : currentPage.includes("site-map")
    setEditDisabled(isDisabled)
  }, [currentClickedPoint])//eslint-disable-line
  
  /** HANDLERS FOR MAP EVENTS **/

  const markerDrag = (event) => {
    if (!poiMap || editDisabled || (!isAdmin && poiIsInGroups)) return;
    handleMarkerDrag(event, polyLineArr)
  }

  const mapClick = (event) => {
    if (!poiMap) return;
    handleMapClick(event, polyLineArr)
  }

  /******************************************************* */
  /* get hover text for agm markers */
  /******************************************************* */
  const getAGMHoverText = (title, passTime, approved, estPassTime, showLabel) => {

    //if AGM labels are showing, just show passtime (or estimated pass time) on hover, if it exists
    if (showLabel && passTime && approved) {
      // return `Pass Time: ${new Date(passTime).toLocaleString()}`
      return `Pass Time: ${convertToLocalTime(passTime, timezone.value)} (${timezone.abbr})`;
    }
    if (showLabel && estPassTime) {
      // return `Est Pass Time: ${new Date(estPassTime).toLocaleString()}`
      return `Est Pass Time: ${convertToLocalTime(estPassTime, timezone.value)} (${timezone.abbr})`;
    }
    if (showLabel && !passTime) {
      return null
    }

    //if no labels showing, show AGM title and passtime (or estimated pass time), if it exists
    if (!showLabel && passTime && approved) {
      // return `${title}, Pass Time: ${new Date(passTime).toLocaleString()}`
      return `${title}, Pass Time: ${convertToLocalTime(passTime, timezone.value)} (${timezone.abbr})`;
    }

    if (!showLabel && estPassTime) {
      // return `${title}, Est Pass Time: ${new Date(estPassTime).toLocaleString()}`
      return `${title}, Est Pass Time: ${convertToLocalTime(estPassTime, timezone.value)} (${timezone.abbr})`;
    }
    return title
  }

  // Render the GoogleMapComponent only if isLoaded and data are available
  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  //return MapComponent
  return (
    <div className='martrak-page-padding-left-right mb-12 flex flex-col'>

      {/* show live track data if run selected and launched */}
        {selectedJob &&
          selectedRun &&
          runData &&
          runData.HasLaunched &&
          (
            <div className="grid grid-cols-1 gap-1 md:grid-cols-2 mt-10">
              {/* run messages area inside of RunMessages context, in order to share messages state */}
              <RunMessagesProvider 
                setNotificationsPaused={setNotificationsPaused}
              >
                <LiveTrackSummary
                  selectedJob={selectedJob.value}
                  selectedRun={selectedRun.value}
                  dataUpdated={dataUpdated}
	                timezone={timezone}
                />

                <div>
                  <RunMessages
                    selectedRun={selectedRun.value}
                    numberOfMessages={runMessageCount}
                    dataUpdated={dataUpdated}
                    notificationsPaused={notificationsPaused}
		                timezone={timezone}
                  />

                  <LiveTrackControl
                    selectedRun={selectedRun.value}
                    notificationsPaused={notificationsPaused}
                  />

                </div>

              </RunMessagesProvider>
            </div>
          )}
          
      {/* dropdown for selecting poi groups */}
      {poiMap && canSelectPOIs && (
        <div className={currentPage.includes("site-map") ? "mt-10" : ""}>
          <Label>Select POIs by Group</Label>
          <SelectContainer>

            {currentPage.includes("site-map") ? (
              <Select
                value={selectedPoiGroup}
                options={poiGroups}
                name="poiGroupDropdown"
                onChange={(x) => setSelectedPoiGroup(x)}
                menuPosition='fixed'
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            ) : (
              <Select
                options={permissionSortedPoiGroups} // Pass the nested options to Select
                value={selectedPoiGroup} // The currently selected value
                onChange={(x) => setSelectedPoiGroup(x)} // Handle selection change
                getOptionLabel={(e) => e.label} // Custom label for each option
                getOptionValue={(e) => e.value} // Custom value for each option
                placeholder="Select a POI Group" // Placeholder text
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}

              />
            )}
            
          </SelectContainer>
          {currentPage.includes("poi-edit") && (
            <Button 
              onClick={() => handleOpenCustomIntersectModal()}
              variant='success'
              className='mb-8'
            >
              <i className="fa-solid fa-location-dot mr-2"></i>
              Add POI using Intersection Point
            </Button>
          )}
          
          {/* Modal for adding custom intersection point */}
          {showCustomIntersectModal && (
            <CustomIntersectModal
              onSubmit={handleCustomIntersectSubmit}
              onCancel={() => setShowCusomIntersectModal(false)}
              validationError={customIntersectError}
              isEdit={false} //creating new point on map
            />
          )}
        </div>
      )}

      {/* notifications paused message */}
      {runData && notificationsPaused  ? (
        <p className="text-red-500 text-md font-bold mb-1 md:mb-0 self-center">
           <><i className="fa-solid fa-circle-exclamation"></i> Notifications are paused. Estimated pass times may be inaccurate.</>
      </p>
      ) : null }

      <MapControl />
      {selectedJob && !agms.length && !isFetching &&
        <div className='flex justify-center mt-20'>
          <p className='text-red-600 font-bold'>No AGMs found for this job - Cannot load map</p>
        </div>
      }

      {!selectedJob &&
        <div className='flex justify-center mt-20'>
          <p className='text-purple-600 font-bold italic'>Select a job to view map...</p>
        </div>
      }



                  
      {agms.length > 0 &&
        <GoogleMapComponent
          mapContainerStyle={containerStyle}
          center={center}
          zoom={14}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={mapClick}
          mapTypeId='satellite'
        >
          {poiMap &&
            clickedPoints.map((point) => (
              <MemoizedMarker
                key={`poi-${point.POIID}`}
                position={point}
                onClick={() => handleMarkerClick(point, polyLineArr)}
                icon={{
                  url: poiMarker,
                  scaledSize: new window.google.maps.Size(24, 24),
                  anchor: new window.google.maps.Point(0, 24),
                }}
                title={(!selectedPoiGroup?.label.includes("All POIs") && point.AltPOI_Name) ? point.AltPOI_Name : point.POI_Name} //on hover, show alt name if it exists, otherwise show original name
                zIndex={60}
              />
            ))
          }
          {poiMap &&
            draggableMarkerPosition &&
            !editDisabled &&
            (!poiIsInGroups || isAdmin) &&  //only show draggable marker if POI not in any groups - unless user is Admin, then they can make changes any time
            (
              <Marker
                position={draggableMarkerPosition}
                draggable={true}
                onDrag={markerDrag}
                zIndex={50}
                icon={{
                  url: draggableMarker,
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 40),
                }}

              />
            )}
          {memoizedAGMPositions.map(agm => (
            <MemoizedMarker
              key={`agm-${agm.id}`}
              position={{ lat: agm.lat, lng: agm.lng }}
              visible={mapControl['agm-toggle']}
              zIndex={50}
              // title={agm.title}
              title={getAGMHoverText(agm.title, agm.PassTime, agm.Approved, agm.Est_PassTime, mapControl['label-toggle'])}
              icon={(agm.PassTime && agm.Approved) ? greenMarker : redMarker}
              label={mapControl['label-toggle'] ? {
                color: '#ddd',
                text: `${agm.title}`,
                fontSize: '12px',
                className: 'custom-label'
              } : null}
            />
          ))}
          {memoizedSCPositions.map(sc => (
            <MemoizedMarker
              key={`sc-${sc.id}`}
              position={{ lat: sc.lat, lng: sc.lng }}
              icon={{
                url: subchainMarker,
                scaledSize: new window.google.maps.Size(10, 10),
              }}
              visible={mapControl['sc-toggle']}
              label={mapControl['sc-label-toggle'] ? {
                color: '#ddd',
                text: `${sc.SC_Name}`,
                fontSize: '12px',
                className: 'custom-label'
              } : null}
            />
          ))}

          {memoizedRoute && memoizedRoute.length > 0 && (
            <Polyline
              path={memoizedRoute}
              options={polylineOptions}
              visible={true}
            />
          )}
          {/* setAgmData passed to pig marker - this updates agmData when there is a change*/}
          <PigMarker
            selectedJob={selectedJob}
            selectedRun={selectedRun}
            liveWPig={liveWPig}
            setAgmData={setAgmData}
            setDataUpdated={setDataUpdated}
            mapRef={mapRef}
            
            
            //testing
            // mergeLiveTrackWithRouteArr={mergeLiveTrackWithRouteArr}
            // findAndStoreLastUpdateValue={findAndStoreLastUpdateValue}
            // mergedLTandRoute={mergedLTandRoute}
            // routeArrRef={routeArrRef}
            // agms={agms}
          />
        </GoogleMapComponent>
      }

    </div>
  );
};

export default React.memo(MapComponent);